/**
 * JobCardTemplateEditor — Iter 413 (Dirk, 2026-02-28).
 *
 * URL:
 *   /jobcards/templates/new                — new template
 *   /jobcards/templates/new?customer_id=X  — new customer-specific
 *   /jobcards/templates/:tid               — edit existing
 *
 * Fields:
 *   • Name (required)
 *   • Description
 *   • Customer scope (Global ↔ specific customer)
 *   • Tasks (add / remove / rename / reorder). Full JC checklist shape.
 *     Reorder is drag-free (arrow buttons) to keep it accessible on
 *     mobile.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Save, Loader2, Plus, Trash2, ArrowUp, ArrowDown, Copy,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import SearchableSelect from "../components/SearchableSelect";
import DictTextarea from "../components/DictTextarea";

function newTaskId() {
  return (crypto?.randomUUID?.() || `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
}

export default function JobCardTemplateEditor() {
  const { tid } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isNew = !tid || tid === "new";
  const customerIdFromQuery = params.get("customer_id") || "";

  const [customers, setCustomers] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [customerId, setCustomerId] = useState(customerIdFromQuery);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cRes, meRes] = await Promise.all([
        api.get("/customers").catch(() => ({ data: [] })),
        api.get("/auth/me").catch(() => ({ data: null })),
      ]);
      setCustomers(Array.isArray(cRes.data) ? cRes.data : (cRes.data?.items || []));
      setMe(meRes.data || null);
      if (!isNew) {
        setLoading(true);
        const { data } = await api.get(`/jobcard-templates/${tid}`);
        setName(data.name || "");
        setDescription(data.description || "");
        setCustomerId(data.customer_id || "");
        setTasks((data.tasks || []).map((t) => ({ ...t, id: t.id || newTaskId() })));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed.");
    } finally {
      setLoading(false);
    }
  }, [tid, isNew]);

  useEffect(() => { load(); }, [load]);

  const canWrite = !!me && ["owner", "admin", "manager"].includes(me.role);

  const customerOptions = useMemo(() => customers.map((c) => ({
    id: c.id,
    label: c.name,
    sublabel: c.external_ref || c.email || c.phone || "",
    searchTerms: [c.external_ref, c.vat_number, c.email, c.phone].filter(Boolean),
  })), [customers]);

  const addTask = () => {
    setTasks((prev) => [...prev, {
      id: newTaskId(),
      label: "",
      status: "pending",
      comment: "",
      comments: [],
      photos: [],
      hidden_from_client: false,
      assignee_ids: [],
      assignee_names: [],
    }]);
  };
  const removeTask = (id) => setTasks((prev) => prev.filter((t) => t.id !== id));
  const updateTask = (id, patch) => setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  const moveTask = (idx, dir) => {
    setTasks((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      const [row] = next.splice(idx, 1);
      next.splice(target, 0, row);
      return next;
    });
  };
  const duplicateTask = (id) => {
    setTasks((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      if (idx < 0) return prev;
      const clone = { ...prev[idx], id: newTaskId(), label: `${prev[idx].label} (copy)` };
      const next = [...prev];
      next.splice(idx + 1, 0, clone);
      return next;
    });
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required."); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        customer_id: customerId || null,
        tasks: tasks.filter((t) => (t.label || "").trim()).map((t) => ({
          ...t,
          label: t.label.trim(),
        })),
      };
      if (isNew) {
        const { data } = await api.post("/jobcard-templates", payload);
        toast.success("Template created.");
        nav(`/jobcards/templates/${data.id}`, { replace: true });
      } else {
        await api.patch(`/jobcard-templates/${tid}`, payload);
        toast.success("Template saved.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="hv-card p-6 m-6 text-center text-[#a0aabf]">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading template…
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="jc-template-editor">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/jobcards/templates" className="hv-btn hv-btn-ghost text-sm"
              data-testid="jc-template-editor-back">
          <ArrowLeft className="w-4 h-4" /> Templates
        </Link>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-[#647087]">Templates</div>
          <div className="text-xl font-bold truncate">{isNew ? "New template" : (name || "Template")}</div>
        </div>
        <button
          onClick={save}
          disabled={saving || !canWrite}
          className="hv-btn hv-btn-primary"
          data-testid="jc-template-editor-save"
          title={canWrite ? "Save" : "Only owner/admin/manager can save."}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </div>

      {!canWrite && (
        <div className="hv-card p-3 border border-amber-500/40 bg-amber-500/5 text-amber-200 text-xs">
          Read-only. Only owner / admin / manager can edit templates.
        </div>
      )}

      {/* Meta */}
      <div className="hv-card p-4 space-y-3">
        <label className="block text-[10px] uppercase tracking-wider text-[#647087] font-bold">
          Template name *
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!canWrite}
            placeholder="e.g. Call-out inspection · Weekly service · Filter change"
            className="hv-input text-sm w-full mt-1"
            data-testid="jc-template-editor-name"
          />
        </label>
        <label className="block text-[10px] uppercase tracking-wider text-[#647087] font-bold">
          Description
          <DictTextarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={!canWrite}
            placeholder="Short note describing when to use this template."
            className="hv-input text-sm w-full mt-1"
            data-testid="jc-template-editor-desc"
          />
        </label>
        <label className="block text-[10px] uppercase tracking-wider text-[#647087] font-bold">
          Customer scope
          {/* Iter 413.2 (Dirk, 2026-02-28) — Searchable typeahead so
              picking a customer doesn't require scrolling through a
              200+ item native <select> on mobile. */}
          <div className="mt-1">
            <SearchableSelect
              value={customerId}
              onChange={(cid) => setCustomerId(cid || "")}
              options={customerOptions}
              placeholder="Global (any customer) — pick a customer to make it customer-specific"
              emptyHint="No matching customers."
              disabled={!canWrite}
              testId="jc-template-editor-scope"
            />
          </div>
        </label>
      </div>

      {/* Tasks */}
      <div className="hv-card p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-sm font-bold flex-1">Tasks</div>
          <div className="text-[10px] text-[#647087]">
            {tasks.length} task{tasks.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="space-y-2">
          {tasks.map((t, idx) => (
            <div key={t.id} className="flex items-start gap-2" data-testid={`jc-template-task-${idx}`}>
              <span className="text-[10px] font-mono text-[#647087] w-6 shrink-0 pt-2">
                {idx + 1}.
              </span>
              <div className="flex-1 min-w-0 space-y-1">
                <input
                  value={t.label}
                  onChange={(e) => updateTask(t.id, { label: e.target.value })}
                  disabled={!canWrite}
                  placeholder="Task label (what should the tech do?)"
                  className="hv-input text-sm w-full"
                  data-testid={`jc-template-task-label-${idx}`}
                />
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="text-[10px] text-[#a0aabf] inline-flex items-center gap-1 select-none">
                    <input
                      type="checkbox"
                      checked={!!t.hidden_from_client}
                      onChange={(e) => updateTask(t.id, { hidden_from_client: e.target.checked })}
                      disabled={!canWrite}
                      data-testid={`jc-template-task-hidden-${idx}`}
                    />
                    Hidden from customer portal
                  </label>
                </div>
              </div>
              {canWrite && (
                <div className="flex items-start gap-0.5 pt-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => moveTask(idx, -1)}
                    disabled={idx === 0}
                    className="text-[#647087] hover:text-[#1FB6C1] disabled:opacity-30 p-1"
                    data-testid={`jc-template-task-up-${idx}`}
                    title="Move up"
                  ><ArrowUp className="w-3 h-3" /></button>
                  <button
                    type="button"
                    onClick={() => moveTask(idx, +1)}
                    disabled={idx === tasks.length - 1}
                    className="text-[#647087] hover:text-[#1FB6C1] disabled:opacity-30 p-1"
                    data-testid={`jc-template-task-down-${idx}`}
                    title="Move down"
                  ><ArrowDown className="w-3 h-3" /></button>
                  <button
                    type="button"
                    onClick={() => duplicateTask(t.id)}
                    className="text-[#647087] hover:text-emerald-300 p-1"
                    data-testid={`jc-template-task-dup-${idx}`}
                    title="Duplicate"
                  ><Copy className="w-3 h-3" /></button>
                  <button
                    type="button"
                    onClick={() => removeTask(t.id)}
                    className="text-rose-300/70 hover:text-rose-300 p-1"
                    data-testid={`jc-template-task-del-${idx}`}
                    title="Remove"
                  ><Trash2 className="w-3 h-3" /></button>
                </div>
              )}
            </div>
          ))}
          {tasks.length === 0 && (
            <div className="text-center py-6 text-xs text-[#647087]">
              No tasks yet. Click <button className="underline" onClick={addTask}>Add task</button>.
            </div>
          )}
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={addTask}
            className="hv-btn hv-btn-ghost text-xs mt-3"
            data-testid="jc-template-editor-add-task"
          >
            <Plus className="w-3 h-3" /> Add task
          </button>
        )}
      </div>
    </div>
  );
}
